import {
  InterceptRuleToolInputSchema,
  INTERCEPT_RULE_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import {
  filterInterceptRulesForList,
  mapInterceptRuleToSummary,
} from '../../../../utils/interceptToolRule.utils';
import {
  useProxyStore,
  type InterceptRule,
} from '../../../../store/proxy-store';
import {
  parsePortalToolHandlerInput,
  resolvePortalApplicationId,
} from '../portal-tool-handler.utils';
import { saveInterceptRulesForApplication } from '../../../../utils/intercept-rules-storage';
import type { PortalToolHandler, PortalToolHandlerResult } from '../types';

function getInterceptRuleStateSnapshot() {
  const proxyStore = useProxyStore.getState();

  return {
    ruleCount: (proxyStore.settings.interceptRules ?? []).length,
  };
}

function buildInterceptRuleFromToolInput({
  ruleInput,
  existingRule,
}: {
  ruleInput: {
    name: string;
    urlPattern: string;
    methods?: string[];
    enabled?: boolean;
    description?: string;
  };
  existingRule?: InterceptRule;
}): InterceptRule {
  return {
    id: existingRule?.id ?? crypto.randomUUID(),
    name: ruleInput.name,
    urlPattern: ruleInput.urlPattern,
    methods: ruleInput.methods ?? existingRule?.methods ?? ['GET'],
    enabled: ruleInput.enabled ?? existingRule?.enabled ?? true,
    description: ruleInput.description ?? existingRule?.description,
  };
}

async function finalizeInterceptRuleMutation({
  applicationId,
  message,
  ruleId,
  ruleName,
}: {
  applicationId: string;
  message: string;
  ruleId?: string;
  ruleName?: string;
}): Promise<PortalToolHandlerResult> {
  try {
    saveInterceptRulesForApplication({
      applicationId,
      rules: useProxyStore.getState().settings.interceptRules ?? [],
    });
  } catch (error: unknown) {
    return {
      status: 'error',
      result: {
        message:
          error instanceof Error
            ? error.message
            : 'Failed to save intercept rules',
      },
    };
  }

  const snapshot = getInterceptRuleStateSnapshot();

  return {
    status: 'success',
    result: {
      message,
      data: {
        ruleId,
        ruleName,
        ruleCount: snapshot.ruleCount,
      },
    },
  };
}

export const interceptRuleHandler: PortalToolHandler = async ({
  input,
  toolCallId,
  batchId,
  applicationId,
}) => {
  const parsedInput = parsePortalToolHandlerInput({
    schema: InterceptRuleToolInputSchema,
    input,
    toolName: INTERCEPT_RULE_TOOL_NAME,
    toolCallId,
    batchId,
  });

  if (!parsedInput.ok) {
    return parsedInput.result;
  }

  const resolvedApplicationId = resolvePortalApplicationId({ applicationId });

  const proxyStore = useProxyStore.getState();

  switch (parsedInput.data.action) {
    case 'list': {
      const allRules = proxyStore.settings.interceptRules ?? [];
      const filteredRules = filterInterceptRulesForList({
        rules: allRules,
        nameFilter: parsedInput.data.nameFilter,
      });
      const rules = filteredRules.map(mapInterceptRuleToSummary);
      const filterSuffix = parsedInput.data.nameFilter?.trim()
        ? ` matching "${parsedInput.data.nameFilter.trim()}"`
        : '';

      return {
        status: 'success',
        result: {
          message:
            rules.length === 0
              ? `No intercept scope rules found${filterSuffix}`
              : `Listed ${rules.length} intercept scope rule${rules.length === 1 ? '' : 's'}${filterSuffix}`,
          data: {
            ruleCount: allRules.length,
            rules,
          },
        },
      };
    }

    case 'add': {
      if (!parsedInput.data.rule) {
        return {
          status: 'error',
          result: { message: 'Rule is required for add action' },
        };
      }

      const rule = buildInterceptRuleFromToolInput({
        ruleInput: parsedInput.data.rule,
      });

      proxyStore.addInterceptRule(rule);

      return finalizeInterceptRuleMutation({
        applicationId: resolvedApplicationId,
        message: `Added intercept scope rule "${rule.name}"`,
        ruleId: rule.id,
        ruleName: rule.name,
      });
    }

    case 'update': {
      if (!parsedInput.data.ruleId || !parsedInput.data.rule) {
        return {
          status: 'error',
          result: {
            message: 'ruleId and rule are required for update action',
          },
        };
      }

      const existingRule = proxyStore.settings.interceptRules?.find(
        (rule) => rule.id === parsedInput.data.ruleId,
      );

      if (!existingRule) {
        return {
          status: 'error',
          result: {
            message: `Intercept scope rule ${parsedInput.data.ruleId} not found`,
          },
        };
      }

      const updates = buildInterceptRuleFromToolInput({
        ruleInput: parsedInput.data.rule,
        existingRule,
      });

      proxyStore.updateInterceptRule(parsedInput.data.ruleId, {
        ...updates,
        id: parsedInput.data.ruleId,
      });

      return finalizeInterceptRuleMutation({
        applicationId: resolvedApplicationId,
        message: `Updated intercept scope rule "${updates.name}"`,
        ruleId: parsedInput.data.ruleId,
        ruleName: updates.name,
      });
    }

    case 'remove': {
      if (!parsedInput.data.ruleId) {
        return {
          status: 'error',
          result: { message: 'ruleId is required for remove action' },
        };
      }

      const removedRule = proxyStore.settings.interceptRules?.find(
        (rule) => rule.id === parsedInput.data.ruleId,
      );

      if (!removedRule) {
        return {
          status: 'error',
          result: {
            message: `Intercept scope rule ${parsedInput.data.ruleId} not found`,
          },
        };
      }

      proxyStore.removeInterceptRule(parsedInput.data.ruleId);

      return finalizeInterceptRuleMutation({
        applicationId: resolvedApplicationId,
        message: `Removed intercept scope rule ${parsedInput.data.ruleId}`,
        ruleId: parsedInput.data.ruleId,
        ruleName: removedRule.name,
      });
    }

    default:
      return {
        status: 'error',
        result: { message: `Unknown action for ${INTERCEPT_RULE_TOOL_NAME}` },
      };
  }
};
